import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
  effect
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { EMPTY, Observable, Subscription, forkJoin, from, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap, toArray } from 'rxjs/operators';
import { GassClaims } from 'src/app/auth/gass-claims';
import { IdentityService } from 'src/app/auth/identity.service';
import { AllegatiService } from 'src/app/core/allegati.service';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentiService } from 'src/app/core/documenti.service';
import { HierarchyService } from 'src/app/core/hierarchy.service';
import { NuovoDocumentoService } from 'src/app/core/nuovo-documento.service';
import { RegistriService } from 'src/app/core/registri.service';
import { SignalrService } from 'src/app/core/signalr.service';
import { MenuService } from 'src/app/layout/services/menu.service';
import { DocumentiListColumnNames } from 'src/app/shared/components/documenti-list/documenti-list.model';
import { autocompleteFilter } from 'src/app/shared/helpers/autocompleteFilter.helper';
import { DistruzioneAllegato } from 'src/app/shared/models/DistruzioneRequest.model';
import { Acquisizione } from 'src/app/shared/models/acquisizione.model';
import { CisAllegato, CisAllegatoRequest, CisClassificaUpdateAllegatoRequest, CisUpdateAllegatoRequest } from 'src/app/shared/models/cisAllegato.model';
import { Classifica } from 'src/app/shared/models/classifica.model';
import { DocumentiSearchDocRow, DocumentiSearchResponse } from 'src/app/shared/models/documento-search.model';
import { Documento } from 'src/app/shared/models/documento.model';
import { AllegatiCondivisiDetails, AllegatiCondivisiRequest, DocumentoAllegato, TipoAllegato } from 'src/app/shared/models/documentoAllegato.model';
import { DocumentoRiproduzioneRow, EnteRichiedente } from 'src/app/shared/models/documentoRiproduzione.model';
import { Indirizzo } from 'src/app/shared/models/indirizzo.model';
import { Posizione } from 'src/app/shared/models/posizione.model';
import { Qualifica } from 'src/app/shared/models/qualifica.model';
import { RegistriResponse } from 'src/app/shared/models/registriRequest.model';
import { StatiPredefiniti } from 'src/app/shared/models/stati-predefiniti';
import { Stato } from 'src/app/shared/models/stato.model';
import { StoricoStatoDocumento } from 'src/app/shared/models/storicoStatoDocumento.model';
import { SaveAllegatoCisEvent } from './components/add-allegato-cis-dialog.component';
import { RegistersTableComponent } from './components/register-table.component';
import { RiproduzioneEvent } from './components/riproduzioni-dialog.component';

@Component({
  selector: 'app-visualizza',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './visualizza.component.html',
  styleUrls: ['./visualizza.component.scss']
})
export class VisualizzaComponent implements OnInit, OnDestroy {
  @ViewChild('registerTable') registerTable: RegistersTableComponent | undefined;

  public GassClaims = GassClaims;
  public readonly TipoAllegato = TipoAllegato;
  public currentDocument = signal<Documento | null>(null);
  public currentDocumentId = -1;
  public allegatiRiproduzione: DocumentoAllegato[] | undefined;
  public allegatidocumentoRiproduzione: DocumentoRiproduzioneRow | undefined;
  public riproduzioneDaTrasmettere: DocumentoRiproduzioneRow | undefined;
  public riproduzioneDaModificare: DocumentoRiproduzioneRow | undefined;
  public distruzioneAllegato: DistruzioneAllegato[] = [];
  public riferimentiSearchResponse: DocumentiSearchResponse = { originalRequest: { pageFirstRow: 0, pageSize: 20, sortField: 'Protocollo', sortOrder: 1 } } as DocumentiSearchResponse;
  public riferimentiColumns: DocumentiListColumnNames = ['Protocollo', 'DataDocumento', 'PosizioneId', 'Oggetto', 'Arrivo', 'ActionButtons'];
  public currentClassifica: Classifica | undefined;
  public currentQualifica: Qualifica | undefined;

  public allegati$: Observable<DocumentoAllegato[]> | undefined;
  public annessi$: Observable<DocumentoAllegato[]> | undefined;
  public allegatiCondivisi$: Observable<AllegatiCondivisiDetails[]> | undefined;
  public cis$: Observable<CisAllegato[]> | undefined;
  public riproduzioni$: Observable<DocumentoRiproduzioneRow[]> | undefined;
  public riferimenti$: Observable<DocumentiSearchResponse> | undefined;
  public posizione$: Observable<Posizione[]> | undefined;
  public copie$: Observable<string[]> | undefined;
  public storico$: Observable<StoricoStatoDocumento[]> | undefined;
  public stati$: Observable<Stato[]> | undefined;
  public registri$: Observable<RegistriResponse> | undefined;
  public showToolbar = signal<boolean>(true);
  public showChangeState = signal<boolean>(true);
  public showStateDialog = signal<boolean>(false);
  public showCopyDialog = signal<boolean>(false);
  public showDeclassificaDialog = signal<boolean>(false);
  public showDistruzioneDialog = signal<boolean>(false);
  public showStateArchiveDialog = signal<boolean>(false);
  public showTemporarySendDialog = signal<boolean>(false);
  public showRiferimentiDialog = signal<boolean>(false);
  public showRiproduzioniDialog = signal<boolean>(false);
  public showTrasmettiRiproduzioniDialog = signal<boolean>(false);
  public showAllegatiRiproduzioneDialog = signal<boolean>(false);
  public showModificaRiproduzioniDialog = signal<boolean>(false);
  public showSpostaFascicoloDialog = signal<boolean>(false);
  public showPartenzaRiproduzioneDialog = signal<boolean>(false);
  public showAddCISDialog = signal<boolean>(false);
  public showUpdateCISDialog = signal<boolean>(false);
  public showModificaNoteDialog = signal<boolean>(false);
  public showModificaDocumentoDialog = signal<boolean>(false);
  public showRicevutaTrasmissioneDialog = signal<boolean>(false);
  public revocaDialogVisible = signal<boolean>(false);
  public isDistruzioneCIS = signal<boolean>(false);
  public editMode = signal<boolean>(false);
  public showAllegatiCondivisiDialog = signal<boolean>(false);
  public positionCopiaPer: Posizione[] | undefined;
  public passwordAllegato = signal<string | null>(null);
  public allegatoCondivisoToUpload = signal<DocumentoAllegato | undefined>(undefined);
  public alleagatiCaricati = signal<string[]>([]);
  public documentoIdDaDistruggere = -2;
  public tipoModifica = '';
  public tipoModificaOptions = [
    { label: 'Rettifica', value: 'Rettifica' },
    { label: 'Integrazione', value: 'Integrazione' },
    { label: 'Annotazione', value: 'Annotazione' }
  ];
  public isRiproduzioneToDestroy = false;
  public autocompleteFilter = autocompleteFilter;
  public currentCisAllegato: CisAllegato | undefined;
  public destinatariArray: Indirizzo[] | undefined;
  public hasRiferimenti = false;
  public allegatiUpdating = signal<boolean>(false);
  public scannedFile = signal<File | null>(null);
  public scanInProgress = signal<boolean>(false);
  public pendingScanRequestId = signal<string | null>(null);

  public isDocDestroyed = signal<boolean>(false);
  public operationsCanUndo = signal<number[]>([]);

  public isBlocked = signal<boolean>(false);

  // public operationsCanUndo$ : Observable<number[]> | undefined;

  private routeSubscription!: Subscription;
  private signalrSubscription!: Subscription;
  private pendingScanSubscription?: Subscription;
  private currentScanRequestId: string | null = null;

  private breadcrumbAdded = false;
  private hierarchy!: string[];

  private readonly registriService = inject(RegistriService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly documentiService: DocumentiService,
    private readonly allegatiService: AllegatiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly nuovoDocumentoService: NuovoDocumentoService,
    private readonly anagraficheService: AnagraficheService,
    private readonly signalrService: SignalrService,
    private readonly menuService: MenuService,
    private readonly hierarchyService: HierarchyService,
    private readonly indentityService: IdentityService,
    private readonly titleService: Title,
    private dp: DatePipe,
  ) {

    effect(() => {
      const doc = this.currentDocument();
      console.log('Effect chiamato:', {
        hasDoc: !!doc,
        docId: doc?.documentoId,
        protocollo: doc?.protocollo,
        stack: new Error().stack?.split('\n')[2]?.trim()
      });
    });

  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    this.signalrSubscription.unsubscribe();
    this.pendingScanSubscription?.unsubscribe();
    this.deleteCurrentPendingScan();
    this.forceEditSubscription?.unsubscribe();
  }

  ngOnInit(): void {

    this.hierarchy = this.hierarchyService.getHierarchyLabels();

    this.routeSubscription = this.route.paramMap.pipe(
      switchMap((params) => of(params.get('id')))
    ).subscribe(id => {
      this.messageService.clear('allegati');
      if (id === null) return;

      this.stati$ = this.anagraficheService.stati.getAll();
      this.nuovoDocumentoService.init();

      this.currentDocumentId = +id;
      this.loadData();


      this.posizione$ = this.documentiService.getPosizione(this.currentDocumentId).pipe(
        tap(p => {
          this.nuovoDocumentoService.setPosizione(p)
          if (!this.breadcrumbAdded && this.currentDocument()) {
            this.breadcrumbAdded = true;
            this.setBreadcrumb(p)
          }
        })

      );
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    });


    this.signalrSubscription = this.signalrService.currentMessage$.subscribe((doc) => {
      if (this.currentDocument()?.documentoId === doc) {
        this.getAllegati();
      }
    });

    this.currentCisAllegato = {} as CisAllegato;
  }

  private loadData(): void {
    this.getDocumento();
    this.getAllegati();
    this.getAllegatiCIS()
    this.searchRiferimenti();
    this.getCopie();
    this.getRiproduzioni();
    this.getRegistri();
  }

  private setBreadcrumb(posizione: Posizione[]): void {
    this.menuService.clearBreadcrumb();
    posizione.forEach((pos, index) => {
      const docType = this.hierarchy[index];
      const label = docType == "Armadio" || docType == "Fascicolo" || !pos.descrizione ? pos.sigla : pos.descrizione
      const re = /\//ig;
      const breadcrumb: MenuItem = {
        label: label,
        routerLink: `/ricerca/${pos.posizioneId.replace(re, "_")}`,
        routerLinkActiveOptions: { exact: true }
      };
      this.menuService.addBreadcrumb(breadcrumb);
    });

    const documento = this.currentDocument();
    if (documento) {
      this.menuService.addBreadcrumb({ label: `${documento.protocollo} del ${this.dp.transform(documento.dataDocumento, "dd/MM/yyyy")}` });
    } else {
      this.menuService.addBreadcrumb({ label: `Documento ${this.currentDocumentId} non trovato` });
    }
  }

  getDocumento() {

    const hasMoveDocumentClaim = this.indentityService.hasClaim(GassClaims.DocSpostamento);
    const hasUndoOperationClaim = this.indentityService.hasClaim(GassClaims.DocAnnullaOperazione);

    this.isDocDestroyed.set(false);

    if (hasUndoOperationClaim) {
      this.documentiService.annullaListaOperazioni(this.currentDocumentId)
        .subscribe(values => this.operationsCanUndo.set([...values]));
    }

    this.isBlocked.set(false);

    this.documentiService.getDocumento(this.currentDocumentId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.setBreadcrumb([]);
          return EMPTY;
        })
      )
      .subscribe(d => {
        this.currentDocument.set(d);
        this.isBlocked.set(d.bloccato);

        if (d) {
          this.documentiService.UpdateDocumentoRecente(d.documentoId).subscribe();
          if (d.storicoStatiDocumento) {
            this.storico$ = from(d.storicoStatiDocumento).pipe(toArray());
          }

          this.titleService.setTitle(`${d.protocollo} del ${this.dp.transform(d.dataDocumento, "dd/MM/yyyy")} - GASS`);
          this.currentClassifica = this.anagraficheService.classifiche.getAsArray().find(e => e.classificaId === d.classificaId);
          this.currentQualifica = this.anagraficheService.qualifiche.getAsArray().find(e => e.qualificaId === d.qualificaId);
          this.forceEdit();
          this.nuovoDocumentoService.setPartenza(d);

          if (d.statoId as StatiPredefiniti === StatiPredefiniti.Versato
            || d.statoId as StatiPredefiniti === StatiPredefiniti.Acquisito) {
            this.showToolbar.set(false);

            if (hasMoveDocumentClaim && d.statoId as StatiPredefiniti === StatiPredefiniti.Versato) {
              this.showToolbar.set(true);
              this.isDocDestroyed.set(true);
            }
          }
          else if (d.statoId as StatiPredefiniti === StatiPredefiniti.Distrutto || d.statoId as StatiPredefiniti === StatiPredefiniti.Annullato) {
            this.showToolbar.set(false);
            this.showChangeState.set(false);

            if (hasMoveDocumentClaim && d.statoId as StatiPredefiniti === StatiPredefiniti.Distrutto) {
              this.showToolbar.set(true);
              this.isDocDestroyed.set(true);
            }
          }
          else {
            this.showToolbar.set(true);
          }
        }
      });
  }

  private forceEditSubscription?: Subscription;

  forceEdit(): void {
    // Pulisci la subscription precedente per evitare memory leak
    this.forceEditSubscription?.unsubscribe();

    this.forceEditSubscription = this.route.queryParams.subscribe((params: Params) => {
      if (!this.currentDocument()?.bloccato && params?.edit) {
        this.editMode.set(params.edit === 'true');
      }
    });
  }

  lockForceEdit(): void {
    void this.router.navigate([], {
      queryParams: {
        'edit': null
      }, queryParamsHandling: 'merge'
    });
  }

  searchRiferimenti() {
    this.cdr.markForCheck();
    this.riferimenti$ = this.documentiService.getRiferimenti(this.currentDocumentId)
      .pipe(
        map(riferimenti => {
          this.hasRiferimenti = riferimenti && riferimenti.length > 0;
          return {
            originalRequest: { pageFirstRow: 0, pageSize: 20, sortField: 'Protocollo', sortOrder: 1 },
            totalCount: riferimenti.length,
            results: riferimenti.map(r => {
              return {
                data: {
                  documentoId: r.documentoId,
                  protocollo: r.protocollo,
                  dataDocumento: r.dataDocumento,
                  posizioneId: r.posizioneId,
                  oggetto: r.oggetto,
                  arrivo: r.arrivo,
                  posizioneTree: r.posizione,
                  type: 'D'
                } as DocumentiSearchDocRow
              }
            })
          } as DocumentiSearchResponse;
        }),
        tap(r => this.riferimentiSearchResponse = r),
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 404) {
            throw err;
          }

          this.hasRiferimenti = false;
          return EMPTY;
        })
      );
  }

  private getCopie() {
    this.cdr.markForCheck();
    this.copie$ = this.documentiService.getCopie(this.currentDocumentId).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 404) {
          throw err;
        }

        return EMPTY;
      })
    );
  }

  getAllegati(): void {
    this.cdr.markForCheck();
    this.allegati$ = this.allegatiService.getAllegati(this.currentDocumentId, true, TipoAllegato.Allegato);
    this.annessi$ = this.allegatiService.getAllegati(this.currentDocumentId, false, TipoAllegato.Annesso);

    this.allegati$.pipe(
      switchMap(allegati =>
        this.annessi$.pipe(
          tap(annessi => {
            const allegatiLocali = allegati.filter(x => !x.fromSharedFolder);
            const annessiLocali = annessi.filter(x => !x.fromSharedFolder);
            this.distruzioneAllegato = [...allegatiLocali, ...annessiLocali]
              .map(el => ({ allegatoId: el.streamId, pagine: null, fileName: el.fileName }));
            this.alleagatiCaricati.set(allegatiLocali.map(el => el.fileName));
            this.allegatiUpdating.set(false);
          })
        )
      )
    ).subscribe();
  }

  allegatoPreview(name: string) {
    this.allegatiService.getAllegatoPreview(name);
  }

  getAllegatiCIS(): void {
    this.cdr.markForCheck();
    this.cis$ = this.allegatiService.getAllegatiCIS(this.currentDocumentId);
  }

  genera(): void {
    // show partenza dialog
    void this.router.navigate(['nuova-partenza']);
  }

  scan(): void {
    if (this.scanInProgress()) {
      return;
    }

    this.pendingScanSubscription?.unsubscribe();
    this.deleteCurrentPendingScan();
    this.scannedFile.set(null);
    this.scanInProgress.set(true);

    this.pendingScanSubscription = this.allegatiService.acquireImage(this.currentDocumentId)
      .pipe(
        tap(request => {
          this.currentScanRequestId = request.scanRequestId;
          this.pendingScanRequestId.set(request.scanRequestId);
        }),
        switchMap(request => this.allegatiService.downloadPendingScan(request.scanRequestId)),
        tap(response => {
          if (!response.body) {
            throw new Error('Il server ha restituito una scansione vuota.');
          }

          const fileName = this.decodePendingScanFileName(response.headers.get('X-Filename'));
          const content = response.body;
          // Il file è già disponibile: non attendere l'evento complete della
          // richiesta HTTP per riabilitare i comandi della modale.
          this.scanInProgress.set(false);
          this.scannedFile.set(new File([content], fileName, { type: content.type || 'application/pdf' }));
        }),
        finalize(() => {
          this.scanInProgress.set(false);
          if (!this.scannedFile()) {
            this.deleteCurrentPendingScan();
          }
        })
      )
      .subscribe({
        error: (error: HttpErrorResponse | Error) => this.uploadError(this.getPendingScanErrorMessage(error))
      });
  }

  cancelPendingScan(): void {
    this.pendingScanSubscription?.unsubscribe();
    this.pendingScanSubscription = undefined;
    this.scannedFile.set(null);
    this.scanInProgress.set(false);
    this.deleteCurrentPendingScan();
  }

  private deleteCurrentPendingScan(): void {
    const scanRequestId = this.currentScanRequestId;
    if (!scanRequestId) {
      this.pendingScanRequestId.set(null);
      return;
    }

    this.currentScanRequestId = null;
    this.pendingScanRequestId.set(null);
    this.allegatiService.deletePendingScan(scanRequestId).subscribe({ error: () => undefined });
  }

  private decodePendingScanFileName(encodedFileName: string | null): string {
    if (!encodedFileName) {
      return 'scansione.pdf';
    }

    try {
      return decodeURIComponent(encodedFileName) || 'scansione.pdf';
    } catch {
      return encodedFileName;
    }
  }

  private getPendingScanErrorMessage(error: HttpErrorResponse | Error): string {
    if (error instanceof HttpErrorResponse && typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    return error.message?.trim() || 'Impossibile completare la scansione.';
  }

  changeState(): void {
    this.showStateDialog.set(true);
  }

  onHideStateDialog(): void {
    this.showStateDialog.set(false);
  }

  editData(): void {
    this.editMode.update(mode => !mode);
  }

  copiaPer(): void {
    this.showCopyDialog.set(true);
  }

  undoEdit(): void {
    this.getDocumento();
    this.lockForceEdit();
    this.editMode.set(false);
  }

  undoOperation(operation: StatiPredefiniti): void {
    let message = 'Sei sicuro di voler procedere con l\'annullamento ';

    if (operation === StatiPredefiniti.Declassificato) {
      message += 'dell\'ultima operazione di Declassifica?';
    }

    if (operation === StatiPredefiniti.Distrutto) {
      message += 'della operazione di Distruzione? <br/>Ricorda di reinserire manualmente gli allegati.';
    }

    if (message.length > 0) {
      this.confirmationService.confirm({
        header: 'Annullamento Operazione',
        message: message,
        accept: () => this.doUndoOperation(operation)
      });
    }
  }

  private doUndoOperation(operation: StatiPredefiniti): void {
    this.documentiService.annullaOperazione(this.currentDocumentId, operation)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Annullamento Operazione',
            detail: 'Errore generico nell\'annullamento dell\'operazione.'
          });
          throw error;
        }),
      )
      .subscribe(() => {

        let message = 'Operazione annullata con successo';

        if (operation === StatiPredefiniti.Distrutto) {
          message += ', ricorda di reinserire i tuoi allegati';
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Annullamento Operazione',
          detail: message
        });

        this.loadData();
      });
  }

  saveEdit(): void {
    this.tipoModifica = '';
    this.showModificaDocumentoDialog.set(true);
  }

  confirmSaveEdit(): void {
    if (!this.tipoModifica) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Tipo di modifica',
        detail: 'Selezionare un tipo di modifica prima di salvare.'
      });
      return;
    }
    this.lockForceEdit();
    this.editMode.set(false);
    const documento = { ...this.currentDocument()!, tipoModifica: this.tipoModifica };
    this.documentiService.updateDocumento(documento).subscribe(() => {
      this.getDocumento();
      this.getRegistri();
    });
    this.showModificaDocumentoDialog.set(false);
  }

  onHideModificaDocumentoDialog(): void {
    this.showModificaDocumentoDialog.set(false);
  }

  addCopy(): void {
    const posizione = this.positionCopiaPer?.reduce((pos: Posizione | null, item: Posizione | null) => item ?? pos, null);
    if (posizione != null) {
      this.documentiService.addCopiaPer(this.currentDocumentId, posizione.posizioneId).subscribe(() => {
        this.getCopie();
      });
    }

    this.clearCopyDialog();
  }

  spostaFascicolo(newPositionFascicolo: Posizione[]): void {
    const posizione = newPositionFascicolo.reduce((pos: Posizione | null, item: Posizione | null) => item ?? pos, null);
    if (posizione != null) {
      this.documentiService.spostaFascicolo(this.currentDocumentId, posizione.posizioneId).pipe(
        tap(() => {
          this.getDocumento();
          this.getRegistri()
          this.getStorico();
        }),
        catchError((error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Documento non spostato'
          });
          throw error;
        }),
        switchMap(() => {
          this.posizione$ = this.documentiService.getPosizione(this.currentDocumentId).pipe(
            tap(p => this.nuovoDocumentoService.setPosizione(p)),
            tap(p => this.setBreadcrumb(p))
          );
          return of();
        })
      ).subscribe();
    }
    this.onHideSpostaFascicolo();
  }

  deleteCopy(event: Posizione): void {
    this.confirmationService.confirm({
      header: 'Cancellazione Copie',
      message: 'Se sicuro di voler cancellare la copia?',
      accept: () => {
        this.documentiService.deleteCopiaPer(this.currentDocumentId, event.posizioneId).subscribe(() => {
          this.getCopie();
        });
      }
    });
  }

  clearCopyDialog(): void {
    this.positionCopiaPer = [];
    this.showCopyDialog.set(false);
  }

  beforeUpload(): void {
    this.messageService.clear('allegati');
  }

  uploadAllegatiCondivisi(allegato: AllegatiCondivisiRequest): void {
    this.allegatiUpdating.set(true);
    this.allegatiService.uploadAllegatiCondivisi(this.currentDocumentId, allegato).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Upload allegato',
        detail: 'Il documento condiviso è stato caricato con successo'
      })
    });
  }

  uploadCompleted(): void {
    this.getAllegati();
  }

  uploadError(errorMessage: string): void {
    this.messageService.add({
      key: 'allegati',
      severity: 'error',
      summary: 'Errore nel caricamento',
      detail: errorMessage
    });
  }

  downloadFile(allegato: DocumentoAllegato): void {
    this.allegatiService.downloadAllegato(allegato);
  }

  openFile(allegato: DocumentoAllegato): void {
    this.allegatiService.openAllegato(allegato);
  }

  deleteFile(allegato: DocumentoAllegato): void {
    this.allegatiService.deleteAllegato(allegato).subscribe(() => {
      this.getAllegati();
      this.allegatiRiproduzione = this.allegatiRiproduzione?.filter(all => all.streamId !== allegato.streamId);
    });
  }

  setPrimaryAllegato(allegato: DocumentoAllegato): void {
    this.allegatiService.setPrimaryAllegato(allegato).subscribe(() => {
      this.getAllegati();
    });
  }

  relaunchOCR(allegato: DocumentoAllegato): void {
    this.confirmationService.confirm({
      message: `Lanciare il riconoscimento caratteri per l'allegato selezionato?`,
      acceptLabel: 'Avvia',
      accept: () => {
        this.allegatiService.startOCR(allegato).subscribe();
      }
    });
  }

  modifyOCR(allegato: DocumentoAllegato): void {
    this.confirmationService.confirm({
      message: `Modificare il contenuto del testo per l'allegato selezionato?`,
      acceptLabel: 'Conferma',
      accept: () => {
        this.allegatiService.modifyOCR(allegato).subscribe();
      }
    });
  }

  showRiproduzione(): void {
    this.showRiproduzioniDialog.set(true);
  }

  hideRiproduzioneDialog(): void {
    this.showRiproduzioniDialog.set(false);
  }

  getRiproduzioni(): void {
    this.cdr.markForCheck();
    this.riproduzioni$ = this.documentiService.getRiproduzioni(this.currentDocumentId);
  }

  addRiproduzione(riproduzione: RiproduzioneEvent): void {
    // mappa riproduzione.richiedenti in un array di EnteRichiedente
    const entiRichiedenti: EnteRichiedente[] = riproduzione.destinatari.map(e => ({ nome: e.nomeIndirizzo, id: e.indirizzoId }));
    this.documentiService
      .addRiproduzione(
        this.currentDocumentId,
        {
          documentoId: this.currentDocumentId,
          posizioneId: riproduzione.posizione.filter(x => x != null).slice(-1)[0].posizioneId,
          trasmesso: false,
          spedito: false,
          richiedente: riproduzione.dettagli.richiedente,
          numeroRichiesta: riproduzione.dettagli.numeroRichiesta,
          dataRichiesta: riproduzione.dettagli.dataRichiesta,
          entiRichiedenti: entiRichiedenti,
          originatore: riproduzione.dettagli.originatore,
          numeroPagine: riproduzione.dettagli.numeroPagine,
          soloAllegati: riproduzione.dettagli.soloAllegati,
          includeDocumentoPrimario: riproduzione.dettagli.includeDocumentoPrimario,
          includeAllegati: riproduzione.dettagli.includeAllegati
        })
      .subscribe({
        next: () => {
          this.showRiproduzioniDialog.set(false);
          this.getRiproduzioni();
          this.getStorico();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore nel salvataggio della riproduzione',
            detail: ''
          });
        }
      });
  }

  changeRiproduzione(documentoAllegato: DocumentoAllegato): void {
    this.confirmationService.confirm({
      header: 'Modifica Riproduzione',
      message: 'Se sicuro di voler modificare la riproduzione?',
      accept: () => {
        this.allegatiService.updateRiproduzione(documentoAllegato).subscribe(() => this.getRiproduzioni());
      }
    });
  }

  public trasmetti(riproduzione: DocumentoRiproduzioneRow): void {
    if (riproduzione.documentoRiproduzioneId) {
      forkJoin({
        dest: this.documentiService.getDestinatari(riproduzione.documentoRiproduzioneId),
        documentoOrigine: this.getDocumentoOrigineRiproduzione(riproduzione)
      }).subscribe(({ dest, documentoOrigine }) => {
        this.nuovoDocumentoService.setPosizioneRiproduzione(riproduzione.posizione!);
        this.nuovoDocumentoService.setTrasmettiRiproduzione(riproduzione, documentoOrigine, dest);
        void this.router.navigate(['trasmetti-riproduzione']);
      })
    }
  }

  public showTrasmettiDialog(riproduzione: DocumentoRiproduzioneRow): void {
    this.riproduzioneDaTrasmettere = { ...riproduzione };
    this.showTrasmettiRiproduzioniDialog.set(true);
  }

  public showSpedisciDialog(riproduzione: DocumentoRiproduzioneRow): void {
    this.confirmationService.confirm({
      header: 'Spedisci Riproduzione',
      message: 'Se sicuro di voler spedire la riproduzione all\'ente richiedente?',
      accept: () => {
        this.documentiService.sendRiproduzione(riproduzione.documentoRiproduzioneId!).subscribe(() => {
          this.getRiproduzioni();
        });
      }
    });
  }

  public showModificaRiproduzioneDialog(riproduzione: DocumentoRiproduzioneRow): void {
    this.riproduzioneDaModificare = { ...riproduzione };
    if (riproduzione.documentoRiproduzioneId) {
      this.documentiService.getDestinatari(riproduzione.documentoRiproduzioneId).subscribe((dest) => {
        this.destinatariArray = dest;
        this.showModificaRiproduzioniDialog.set(true);
      })
    }
  }

  public onShowAddCISDialog(): void {
    this.currentCisAllegato = undefined;
    this.showAddCISDialog.set(true);
  }

  public onShowModificaCISDialog(item: CisAllegato | undefined): void {
    this.currentCisAllegato = item;
    this.showAddCISDialog.set(true);
  }

  public distruggiRiproduzione(riproduzione: DocumentoRiproduzioneRow): void {
    if (!riproduzione.documentoRiproduzioneId) return;

    this.allegatiService.getAllegati(riproduzione.documentoRiproduzioneId).subscribe(all => {
      this.distruzioneAllegato = all.map(el => ({ allegatoId: el.streamId, pagine: null, fileName: el.fileName }));
      this.documentoIdDaDistruggere = riproduzione.documentoRiproduzioneId!;
      this.isRiproduzioneToDestroy = true;
      this.showDistruzioneDialog.set(true);
    });
  }

  public modificaRiproduzione(event: { riproduzione: DocumentoRiproduzioneRow, destinatari: Indirizzo[] }): void {
    const entiRichiedenti: EnteRichiedente[] = event.destinatari.map(e => ({ nome: e.nomeIndirizzo, id: e.indirizzoId }));
    event.riproduzione.entiRichiedenti = entiRichiedenti;
    this.documentiService.modifyRiproduzione(event.riproduzione).subscribe(
      () => {
        this.showRiproduzioniDialog.set(false);
        this.getRiproduzioni();
      }
    );
  }

  showDeclassification(): void {
    this.showDeclassificaDialog.set(true);
  }

  onHideDeclassifica(): void {
    this.showDeclassificaDialog.set(false);
  }

  showDistruzione(): void {
    this.isRiproduzioneToDestroy = false;
    this.documentoIdDaDistruggere = this.currentDocumentId;
    this.showDistruzioneDialog.set(true);
  }

  onHideDistruzione(): void {
    this.showDistruzioneDialog.set(false);
  }

  onDestroyDone(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Informazione',
      detail: "L'operazione di distruzione è terminata con successo"
    });
    this.getDocumento();
    this.getAllegati();
    this.getAllegatiCIS();
    if (this.isRiproduzioneToDestroy) {
      this.getRiproduzioni();
      //this.getAllegatiCIS();
      this.getRegistri();
    }

    //this.loadData();
  }

  onDestroyFail(error: HttpErrorResponse): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail: error.status == 400 ? error.error : "Errore generico nella distruzione della riproduzione del documento"
    });
  }

  showRevokeDialog(): void {
    this.revocaDialogVisible.set(true);
  }

  onHideRevocaDialog(): void {
    this.revocaDialogVisible.set(false);
  }

  onRevocaDone(): void {
    this.getDocumento();
    this.getStorico();
    this.getRegistri();
  }

  onRevocaFail() {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore nell\'anullamento del protocollo'
    });
  }

  showStateArchive(): void {
    this.showStateArchiveDialog.set(true);
  }

  onHideStateArchiveDialog(): void {
    this.showStateArchiveDialog.set(false);
  }

  sendToArchive(): void {
    this.documentiService.changeState(this.currentDocumentId, 1, '', '').subscribe(() => this.getDocumento());
  }

  showRiferimenti(): void {
    this.showRiferimentiDialog.set(true);
  }

  onHideRiferimenti(): void {
    this.showRiferimentiDialog.set(false);
  }

  addLink(linkId: number): void {
    this.documentiService.addRiferimento(this.currentDocumentId, linkId).subscribe(() => this.searchRiferimenti());
  }

  removeLink(linkdeDocument: DocumentiSearchDocRow): void {
    this.confirmationService.confirm({
      header: 'Cancellazione Riferimento',
      message: `Sei sicuro di voler cancellare il riferimento al protocollo <strong>${linkdeDocument.protocollo}</strong>?`,
      accept: () => {
        this.documentiService.deleteRiferimento(this.currentDocumentId, linkdeDocument.documentoId).subscribe(() => this.searchRiferimenti());
      }
    });
  }

  showTemporarySend(): void {
    this.showTemporarySendDialog.set(true);
  }

  onHideTemporarySendDialog(): void {
    this.showTemporarySendDialog.set(false);
  }

  temporarySend(event: Acquisizione): void {
    this.documentiService.temporarySend(this.currentDocumentId, event).subscribe(() => this.getDocumento());
  }

  unlock(): void {
    this.documentiService.unlock(this.currentDocumentId).subscribe(() => this.getDocumento());
  }

  lock(): void {
    this.documentiService.lock(this.currentDocumentId).subscribe(() => this.getDocumento());
  }

  printLabel(): void {
    this.documentiService.printLabel(this.currentDocumentId);
  }

  showPassword(allegato: DocumentoAllegato): void {
    this.allegatiService.getPassword(allegato).subscribe(p => this.passwordAllegato.set(p.password));
  }

  onHideShowPassword(): void {
    this.passwordAllegato.set(null);
  }

  gestisciAllegatiRiproduzione(documentoRiproduzione: DocumentoRiproduzioneRow): void {
    this.allegatiService.getAllegati(documentoRiproduzione.documentoRiproduzioneId!).subscribe(all => {
      this.allegatiRiproduzione = all;
      this.allegatidocumentoRiproduzione = documentoRiproduzione;
      this.showAllegatiRiproduzioneDialog.set(true);
    });
  }

  onHideAllegatiRiproduzione(): void {
    this.showAllegatiRiproduzioneDialog.set(false);
  }

  moveFascicolo(): void {
    this.showSpostaFascicoloDialog.set(true);
  }

  onHideSpostaFascicolo(): void {
    this.showSpostaFascicoloDialog.set(false);
  }

  onHidePartenzaRiproduzione(): void {
    this.showPartenzaRiproduzioneDialog.set(false);
  }

  onHideAllegatoCIS(): void {
    this.showAddCISDialog.set(false);
  }

  riproduciAllegati(): void {
    this.showPartenzaRiproduzioneDialog.set(true);
  }

  public onDistruggiCIS(cisAllegato: CisAllegato): void {
    this.currentCisAllegato = cisAllegato;
    this.isDistruzioneCIS.set(true);
    this.showUpdateCISDialog.set(true);
  }

  public showUploadAllegatoCondivisoDialog(file: DocumentoAllegato): void {
    this.allegatoCondivisoToUpload.set(file);
    this.showAllegatiCondivisiDialog.set(true);
  }

  public onHideUploadAllegatoCondivisoDialog(): void {
    this.showAllegatiCondivisiDialog.set(false);
  }

  public onRecuperaCIS(cisAllegato: CisAllegato): void {
    const numeriOrdine = [cisAllegato.numeroOrdine];
    this.isDistruzioneCIS.set(false);
    this.confirmationService.confirm({
      header: 'Annulla distruzione',
      message: 'Recupera allegato CIS',
      accept: () => this.doOnRecuperaCIS(numeriOrdine)
    });
  }

  private doOnRecuperaCIS(numeriOrdine: number[]): void {
    this.documentiService.annullaDistruzioneAllegatoCis(this.currentDocumentId, numeriOrdine)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Recupero Allegato CIS',
            detail: 'Errore generico nel recupero dell\'allegato CIS.'
          });
          throw error;
        }),
      )
      .subscribe(() => {

        this.messageService.add({
          severity: 'success',
          summary: 'Recupero Allegato CIS',
          detail: 'Allegato CIS recuperato con successo'
        });

        this.loadData();
      });
  }

  public onRestituisciCIS(cisAllegato: CisAllegato): void {
    this.currentCisAllegato = cisAllegato;
    this.isDistruzioneCIS.set(false);
    this.showUpdateCISDialog.set(true);
  }

  onHideUpdateCISDialog(): void {
    this.showUpdateCISDialog.set(false);
  }

  public onUpdateCIS(cisUpdateAllegatoRequest: CisUpdateAllegatoRequest): void {
    this.allegatiService.updateAllegatoCIS(cisUpdateAllegatoRequest).subscribe(() => {
      this.getStorico();
      this.getAllegatiCIS();
      this.getRegistri();
    });
  }

  trasmettiAllegati(): void {
    const documento = this.currentDocument();
    if (!documento) return;
    this.nuovoDocumentoService.setPartenza(documento);
    void this.router.navigate(['trasmetti-originali']);
  }

  generaPartenza(event: string): void {
    switch (event) {
      case 'T':
        //Trasmetti
        break;
      case 'R': {
        const nuovoDoc = this.nuovoDocumentoService.documento;
        const nuovaRiproduzione: DocumentoRiproduzioneRow = {
          documentoId: this.currentDocumentId,
          posizione: this.nuovoDocumentoService.posizione,
          posizioneId: this.isText(nuovoDoc.posizioneId) ? nuovoDoc.posizioneId : '',
          trasmesso: false,
          spedito: false,
          richiedente: '',
          numeroRichiesta: '',
          dataRichiesta: new Date(),
          entiRichiedenti: [],
          //enteRichiedenteId: 0,
          originatore: '',
          numeroPagine: '',
          soloAllegati: false,
          includeDocumentoPrimario: true,
          includeAllegati: true
        }

        this.documentiService
          .addRiproduzione(
            this.currentDocumentId,
            nuovaRiproduzione)
          .pipe(
            switchMap(riproduzioneCreata => this.getDocumentoOrigineRiproduzione(nuovaRiproduzione).pipe(
              map(documentoOrigine => ({ riproduzioneCreata, documentoOrigine }))
            ))
          )
          .subscribe(({ riproduzioneCreata, documentoOrigine }) => {
            const riproduzioneDaTrasmettere = {
              ...nuovaRiproduzione,
              ...riproduzioneCreata,
              documentoId: this.currentDocumentId,
              posizione: nuovaRiproduzione.posizione,
              posizioneId: nuovaRiproduzione.posizioneId
            } as DocumentoRiproduzioneRow;

            this.nuovoDocumentoService.setPosizioneRiproduzione(riproduzioneDaTrasmettere.posizione!);
            this.nuovoDocumentoService.setTrasmettiRiproduzione(riproduzioneDaTrasmettere, documentoOrigine, []);
            void this.router.navigate(['trasmetti-riproduzione']);
          });
      }
        break;
      default:
        // Do nothing
        this.genera();
        break;
    }
  }

  public isText(data: unknown): data is string {
    return typeof data === 'string';
  }

  private getDocumentoOrigineRiproduzione(riproduzione: DocumentoRiproduzioneRow): Observable<Documento> {
    const currentDocument = this.currentDocument();
    const documentoOrigineId = riproduzione.documentoId || currentDocument?.documentoId || this.currentDocumentId;

    if (documentoOrigineId > 0) {
      return this.documentiService.getDocumento(documentoOrigineId).pipe(
        catchError(() => currentDocument ? of(currentDocument) : EMPTY)
      );
    }

    return currentDocument ? of(currentDocument) : EMPTY;
  }

  onSaveAllegatoCIS(event: SaveAllegatoCisEvent): void {
    if (!event.source) {
      this.addAllegatoCIS(event);
    } else {
      this.updateAllegatoCIS(event);
    }
  }

  private addAllegatoCIS(event: SaveAllegatoCisEvent): void {
    const noteAllegato = ((event.note && event.note !== '') ? event.note + ' - ' : '') + 'allegato a: ' + this.currentDocument()!.protocollo;

    const allegatoCIS: CisAllegatoRequest = {
      documentoId: this.currentDocumentId,
      tipoCIS: event.tipoCIS,
      caricoRicevente: event.ricevente,
      caricoFirma: event.firma,
      classifica: event.classifica.nomeClassifica,
      qualifica: event.qualifica.nomeQualifica,
      riproduzione: event.riproduzione,
      note: noteAllegato,
      registroData: this.currentDocument()!.dataProtocollazione!,
      numeroCopie: event.numeroCopie,
      annotazioniUtente: event.annotazioniUtente
    }

    this.allegatiService.insertAllegatoCIS(allegatoCIS).subscribe(() => {
      this.getStorico();
      this.getAllegatiCIS();
      this.getRegistri();
    });
  }

  private updateAllegatoCIS(event: SaveAllegatoCisEvent): void {

    const allegatoCISOrig = event.source;

    if (allegatoCISOrig) {
      const allegatoCIS: CisClassificaUpdateAllegatoRequest = {
        documentoId: allegatoCISOrig.documentoId,
        classificaId: event.classifica.classificaId,
        classificaIdCorrente: event.classificaIdCorrente,
        numeroOrdine: allegatoCISOrig.numeroOrdine,
        registroData: allegatoCISOrig.registroData,
        qualifica: event.qualifica.nomeQualifica,
        classifica: event.classifica.nomeClassifica,
        note: allegatoCISOrig.note,
        annotazioniUtente: event.annotazioniUtente
      }

      this.allegatiService.updateClassificaAllegatoCIS(allegatoCIS).subscribe(() => {
        this.getStorico();
        this.getAllegatiCIS();
        this.getRegistri();
      });
    }
  }

  editNote() {
    this.showModificaNoteDialog.set(true);
  }

  onNoteChanged(note: string): void {
    if (this.currentDocument()?.note === note) {
      this.showModificaNoteDialog.set(false);
      return;
    }

    const documento = { ...this.currentDocument()!, note: note };
    this.documentiService.updateDocumento(documento).subscribe({
      next: () => {
        this.showModificaNoteDialog.set(false);
        this.currentDocument.set(documento);
        console.log('currentDocument updated via onNoteChanged', documento.protocollo, documento.dataProtocollazione);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel salvataggio delle note',
          detail: ''
        });
      }
    });
  }

  onHideModificaNote(): void {
    this.showModificaNoteDialog.set(false);
  }

  stampaRicevuta(): void {
    this.showRicevutaTrasmissioneDialog.set(true);
  }

  onHideRicevutaTrasmissione(): void {
    this.showRicevutaTrasmissioneDialog.set(false);
  }

  scaricaSignXml(): void {
    this.documentiService.scaricaSignXml(this.currentDocumentId);
  }

  scaricaDocumentoAmministrativoXml(): void {
    this.documentiService.scaricaDocumentoAmministrativoXml(this.currentDocumentId);
  }

  onDocumentoChange(updated: Documento | null): void {}

  private getStorico(): void {
    this.cdr.markForCheck();
    this.storico$ = this.documentiService.getStorico(this.currentDocumentId);
  }

  private getRegistri(): void {
    this.cdr.markForCheck();
    this.registri$ = this.registriService.getByDocumentId(this.currentDocumentId)
  }

  public getTitle(): string {
    return this.titleService.getTitle();
  }

  public getDatePipe(): DatePipe {
    return this.dp;
  }

  public setDatePipe(value: DatePipe): void {
    this.dp = value;
  }

  public setBreadcrumbAdded(value: boolean): void {
    this.breadcrumbAdded = value;
  }

  public getBreadcrumbAdded(): boolean {
    return this.breadcrumbAdded;
  }
}
